/**
 * Global error boundary — wraps the router shell.
 *
 * - Renders a non-destructive fallback when a render throws.
 * - Fires a toast for surfaced 5xx errors. (4xx other than 401 are
 *   typically rendered inline by the consuming component; 401 is
 *   handled by the router / useMe path.)
 *
 * This is the canonical pattern for slice 3 onward — every component
 * surface uses this boundary + the toast hook rather than rolling
 * its own error UI.
 */

import * as React from "react";
import { ServerError } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

type Props = {
  children: React.ReactNode;
};

type State = {
  error: Error | null;
};

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    if (error instanceof ServerError) {
      toast({
        variant: "destructive",
        title: "Server error",
        description:
          "Something went wrong loading that. The team's been notified — try again or refresh.",
      });
    }
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
          <h1 className="text-2xl">Something broke</h1>
          <p className="max-w-md text-sm text-plane-muted">
            Command Center hit an unexpected error rendering this view.
            Refreshing usually fixes it. If it keeps happening, ping the
            engineering channel.
          </p>
          <pre className="max-w-2xl whitespace-pre-wrap rounded-md border bg-muted p-3 text-left text-xs text-muted-foreground">
            {this.state.error.message}
          </pre>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
